/**
 * StarterSelector component - Hero-style starter template selection
 *
 * Displays starter templates as polished cards with philosophy descriptions,
 * gate/dimension counts, and fork action. Shows all 3 unified starters.
 *
 * "Steve Jobs style" — starters feel great out of the box, complexity is opt-in.
 */

import {
  Check,
  CheckCircle,
  Shield,
  Settings,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ALL_STARTER_TEMPLATES } from '@/constants/starter-templates';
import { cn } from '@/lib/utils';
import type { StarterTemplate } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface StarterSelectorProps {
  onSelect: (starterKey: string) => void;
  selectedKey?: string | null;
  compact?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const ICON_MAP: Record<string, LucideIcon> = {
  Shield,
  Zap,
  CheckCircle,
  Settings,
};

function getIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] ?? Settings;
}

/** Build a human-readable summary of what the starter includes */
function buildSummary(template: StarterTemplate): {
  gateCount: number;
  criticalCount: number;
  standardCount: number;
  dimensionCount: number;
  hasKpis: boolean;
  ruleCount: number;
} {
  const criticalCount = template.gates.filter((g) => g.role === 'critical').length;
  const standardCount = template.gates.filter((g) => g.role === 'standard').length;
  return {
    gateCount: template.gates.length,
    criticalCount,
    standardCount,
    dimensionCount: template.dimensions.length,
    hasKpis: template.kpiConfig !== null,
    ruleCount: template.verdictRules.length,
  };
}

/** Derive a concise use-case label from the template's philosophy string */
function getUseCaseLabel(philosophy: string): string {
  const p = philosophy.toLowerCase();
  // Check conversion/direct response BEFORE brand — direct_response philosophy contains "brand building" text
  if (p.includes('conversion') || p.includes('direct response')) {
    return 'Direct Response';
  }
  if (p.includes('brand equity') || p.includes('long-term brand')) {
    return 'Brand Building';
  }
  if (p.includes('pass/fail') || p.includes('catch errors')) {
    return 'Quality Check';
  }
  return 'General';
}

// ============================================================================
// STARTER CARD
// ============================================================================

interface StarterCardProps {
  template: StarterTemplate;
  isSelected: boolean;
  onSelect: (key: string) => void;
  compact?: boolean;
}

function StarterCard({ template, isSelected, onSelect, compact }: StarterCardProps) {
  const Icon = getIcon(template.icon);
  const summary = buildSummary(template);
  const useCaseLabel = getUseCaseLabel(template.philosophy);

  return (
    <Card
      className={cn(
        'relative flex flex-col gap-4 cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:-translate-y-0.5',
        compact ? 'p-4' : 'p-5',
        isSelected
          ? 'border-2 border-brand shadow-md ring-2 ring-brand/20'
          : 'border border-border-subtle hover:border-brand/40',
      )}
      onClick={() => onSelect(template.key)}
    >
      {/* Use-case label badge */}
      <div className="absolute -top-2.5 left-4">
        <span className="text-[10px] font-medium uppercase tracking-wider bg-surface-subtle text-secondary px-2 py-0.5 rounded-full">
          {useCaseLabel}
        </span>
      </div>

      {/* Icon + Selected indicator */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex items-center justify-center rounded-lg',
            compact ? 'w-8 h-8' : 'w-10 h-10',
            isSelected
              ? 'bg-brand/10 text-brand'
              : 'bg-surface-subtle text-secondary',
          )}
        >
          <Icon size={compact ? 16 : 20} />
        </div>

        {isSelected && (
          <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-brand">
            <Check size={14} />
            Selected
          </span>
        )}
      </div>

      {/* Name + Philosophy */}
      <div className="flex flex-col gap-1">
        <p className={cn(
          'font-bold text-primary leading-snug',
          compact ? 'text-sm' : 'text-md',
        )}>
          {template.name}
        </p>
        <p className="text-sm text-secondary leading-normal">
          {template.philosophy}
        </p>
      </div>

      {/* Description */}
      {!compact && (
        <p className="text-xs text-muted leading-relaxed flex-1">
          {template.description}
        </p>
      )}

      {/* Count badges */}
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 bg-surface-subtle text-secondary text-xs px-2 py-0.5 rounded-full">
          {summary.gateCount} gates
        </span>
        <span className="inline-flex items-center gap-1 bg-surface-subtle text-secondary text-xs px-2 py-0.5 rounded-full">
          {summary.dimensionCount} dimensions
        </span>
        {summary.hasKpis && (
          <span className="inline-flex items-center gap-1 bg-surface-subtle text-secondary text-xs px-2 py-0.5 rounded-full">
            Auto KPI
          </span>
        )}
        <span className="inline-flex items-center gap-1 bg-surface-subtle text-secondary text-xs px-2 py-0.5 rounded-full">
          {summary.ruleCount} rules
        </span>
      </div>

      {/* Select button */}
      <Button
        variant={isSelected ? 'primary' : 'secondary'}
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(template.key);
        }}
        className="w-full"
        leftIcon={isSelected ? <Check className="h-4 w-4" /> : undefined}
      >
        {isSelected ? 'Selected' : 'Use This Template'}
      </Button>
    </Card>
  );
}

// ============================================================================
// STARTER SELECTOR
// ============================================================================

export function StarterSelector({
  onSelect,
  selectedKey,
  compact,
}: StarterSelectorProps) {
  return (
    <div
      className={cn(
        'grid gap-4',
        compact
          ? 'grid-cols-1 sm:grid-cols-3'
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      )}
    >
      {ALL_STARTER_TEMPLATES.map((template) => (
        <StarterCard
          key={template.key}
          template={template}
          isSelected={selectedKey === template.key}
          onSelect={onSelect}
          compact={compact}
        />
      ))}
    </div>
  );
}
